import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Image,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { FilledBarIcon, TrophyIcon, WeightIcon } from '../icons';
import { NestedGridTable } from '../NestedGridTable/NestedGridTable';
import { useMemo } from 'react';
import { useGoalsDataGql } from './graphql';
import { columns } from './columns';
import { formatGoalStatus } from '../shared/utils';
import { KeyResultsDetail } from './KeyResultsDetail';
import { Link } from 'react-router-dom';
import goalsZeroState from './shared/assets/img/goals-zero-state.png';
import { useFetchThreshold } from '../metricsCard/apis';
import { Goal, KeyResult } from './types';
import { Row } from '@tanstack/react-table';

interface GoalsTableProps {
  scope?: string;
  author?: string;
  teamId?: string;
}

const CustomLoadingState = () => (
  <Flex direction="column" justify="center" align="center" h="200px" w="100%">
    <Spinner size="lg" color="primary" thickness="2px" speed="0.65s" mb={4} />
    <Text color="text.secondary2" fontSize="md">
      Loading goals data...
    </Text>
  </Flex>
);

const CustomNoDataView = () => (
  <Flex
    direction="column"
    justify="center"
    align="center"
    minH="60vh"
    w="100%"
    gap={'8px'}
  >
    <Image
      src={goalsZeroState}
      alt="goals-zero-state"
      w={'532px'}
      h={'264px'}
      mt={4}
    />
    <Text color="text.gray.300" fontSize="36px" fontWeight="semibold" mt={4}>
      No goals found!
    </Text>
    <Text color="text.secondary2" fontSize="md" fontWeight="normal">
      Start tracking your progress by setting a goal.
    </Text>
    <Button variant="filled" size="md" mt={4}>
      <Link to="/goal/create">Create Goal</Link>
    </Button>
  </Flex>
);

export const GoalsTable = ({ scope, author, teamId }: GoalsTableProps) => {
  const { data, isLoading } = useGoalsDataGql(scope, author, teamId);
  const { data: thresholdData, isLoading: isThresholdLoading } =
    useFetchThreshold('GOAL_DASHBOARD', 'Organisation');

  const rows = useMemo(() => data || [], [data]);

  const dataColumns = useMemo(
    () => columns(thresholdData?.dto?.fields[0]),
    [thresholdData]
  );

  return (
    <div>
      <NestedGridTable
        showLoader={false}
        columns={dataColumns}
        data={rows}
        hideHeaderTooltip
        maxH="65vh"
        sticky="none"
        renderSubComponent={RenderSubComponent}
        disableRowHover
        getSubRows={(rows: Goal[]) => []}
        getRowCanExpand={(row: Row<Goal>) =>
          row?.original?.keyResults && row?.original?.keyResults?.length > 0
        }
        noDataView={isLoading ? <CustomLoadingState /> : <CustomNoDataView />}
      />
    </div>
  );
};

const RenderSubComponent = ({
  row,
  firstColumnWidth,
}: {
  row: Row<Goal>;
  firstColumnWidth: number | undefined;
}) => {
  return (
    <Flex>
      <Box px="24px" height="auto" bg="#FBFBFB" borderRight="2px solid #EDF2F7">
        <Box height="full" width={`${firstColumnWidth}px`} position="relative">
          <Box
            position="absolute"
            top={0}
            left={'7.5px'}
            bg="text.secondary2"
            h={`calc(100% - 34px)`}
            w="1px"
          />
          <Box
            position="absolute"
            h="1px"
            w="10px"
            bg="text.secondary2"
            bottom={'33px'}
            left={'7.5px'}
          />
        </Box>
      </Box>

      <Box w="full">
        <Accordion allowToggle w="full">
          {row.original.keyResults?.map((item: KeyResult, index: number) => (
            <AccordionItem key={item?.id}>
              <Box borderBottom="1px solid" borderColor="text.tertiary">
                <AccordionButton>
                  <Flex align="center" width="full">
                    <Flex py={4} align="center" gap={2}>
                      <TrophyIcon />
                      <Text color={'text.gray.300'} fontWeight={'medium'}>
                        {index + 1}. {item?.title}
                      </Text>
                      <AccordionIcon />
                    </Flex>
                    <Flex ml="auto" gap={8}>
                      <Flex align={'center'} gap={2}>
                        <Box
                          w={'160px'}
                          h={'8px'}
                          rounded={'4px'}
                          bg={'#E9E9E9'}
                        >
                          <Box
                            bg={item?.color}
                            w={`${item?.successRate}%`}
                            h={'full'}
                            rounded={'4px'}
                          />
                        </Box>
                        <Text color={'text.gray.300'} fontWeight={'medium'}>
                          {item?.successRate}%
                        </Text>
                      </Flex>
                      <Flex
                        bg={`${item?.color}1A`}
                        p={'4px'}
                        rounded={'4px'}
                        w={'90px'}
                        align={'center'}
                        justify={'center'}
                        gap={1}
                      >
                        <FilledBarIcon color={item?.color} />
                        <Text
                          color={item?.color}
                          fontWeight={'medium'}
                          fontSize={'sm'}
                        >
                          {formatGoalStatus(item?.status)}
                        </Text>
                      </Flex>
                      <Flex align={'center'} gap={2}>
                        <WeightIcon />
                        <Text
                          fontWeight={'normal'}
                          color={'text.gray.300'}
                          fontSize={'sm'}
                        >
                          {item?.weight}%
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </AccordionButton>
              </Box>
              <AccordionPanel pb={4}>
                <KeyResultsDetail
                  data={item}
                  scope={row.original.scope}
                  goalStartDate={row.original.startDate}
                  goalEndDate={row.original.endDate}
                />
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Box>
    </Flex>
  );
};

export default GoalsTable;
